import { useEffect, useRef } from 'react';
import clsx from 'clsx';

// Global GLightbox instance - initialized once for all galleries
let glightboxInstance = null;
const THUMBSTRIP_CLASS = 'glightbox-thumbstrip';
const THUMBSTRIP_ACTIVE_CLASS = 'glightbox-thumbstrip__thumb--active';

function addHighlightThumbstrip(instance) {
  if (!instance?.elements?.length || !instance.modal) return;
  if (instance.elements.length < 2) return;

  const container = instance.modal.querySelector('.gcontainer');
  if (!container) return;

  const strip = document.createElement('div');
  strip.className = THUMBSTRIP_CLASS;
  strip.setAttribute('aria-label', 'Gallery thumbnails');

  instance.elements.forEach((el, i) => {
    const href = el.slideConfig?.href;
    if (!href) return;
    const thumb = document.createElement('button');
    thumb.type = 'button';
    thumb.className = 'glightbox-thumbstrip__thumb' + (i === instance.index ? ` ${THUMBSTRIP_ACTIVE_CLASS}` : '');
    thumb.setAttribute('aria-label', `View image ${i + 1}`);
    thumb.dataset.index = String(i);
    const img = document.createElement('img');
    img.src = href;
    img.alt = '';
    img.loading = 'lazy';
    thumb.appendChild(img);
    thumb.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      instance.goToSlide(i);
    });
    strip.appendChild(thumb);
  });

  container.appendChild(strip);

  const updateActive = () => {
    const existing = instance.modal?.querySelector(`.${THUMBSTRIP_CLASS}`);
    if (!existing) return;
    const idx = typeof instance.getActiveSlideIndex === 'function' ? instance.getActiveSlideIndex() : instance.index;
    existing.querySelectorAll('.glightbox-thumbstrip__thumb').forEach((t, i) => {
      t.classList.toggle(THUMBSTRIP_ACTIVE_CLASS, i === idx);
    });
  };

  instance.on('slide_changed', updateActive);
}

function removeHighlightThumbstrip(instance) {
  if (!instance?.modal) return;
  const strip = instance.modal.querySelector(`.${THUMBSTRIP_CLASS}`);
  if (strip) strip.remove();
}

const GlightBox = ({
  children,
  image,
  'data-gallery': galleryName = 'gallery',
  ...other
}) => {
  const ref = useRef(null);
  const galleryKey = galleryName || 'gallery';

  useEffect(() => {
    let cancelled = false;
    import('glightbox').then((mod) => {
      if (cancelled) return;
      const GLightbox = mod.default || mod;
      if (!glightboxInstance && document.querySelectorAll('[data-glightbox]').length > 0) {
        glightboxInstance = GLightbox({
          selector: '[data-glightbox]',
          openEffect: 'fade',
          closeEffect: 'fade',
          slideEffect: 'slide',
          moreText: 'See more',
          moreLength: 60,
          closeButton: true,
          touchNavigation: true,
          touchFollowAxis: true,
          keyboardNavigation: true,
          closeOnOutsideClick: true,
          loop: false,
          autoplayVideos: true,
          autofocusVideos: false,
          width: 900,
          height: 'auto',
          onOpen() {
            addHighlightThumbstrip(glightboxInstance);
          },
          onClose() {
            removeHighlightThumbstrip(glightboxInstance);
          },
        });
      }
    }).catch(() => {});

    return () => {
      cancelled = true;
      if (glightboxInstance && document.querySelectorAll('[data-glightbox]').length === 0) {
        glightboxInstance.destroy();
        glightboxInstance = null;
      }
    };
  }, []);

  return (
    <a 
      ref={ref} 
      href={image} 
      data-glightbox
      data-gallery={galleryKey}
      data-title={other['data-title'] || ''}
      {...other} 
      className={clsx('glightbox', other['className'])}
    >
      {children}
    </a>
  );
};

export default GlightBox;